import {
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSInfiniteScroll,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { useQueryRequirementDefinitionList } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionList";
import { useQueryRequirementDefinitionListSize } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionListSize";
import { WSQueries } from "../../../../query/WSQuery";
import {
  getCreateRequirementDefinitionPathname,
  getSectionPathByRequirementStatus
} from "../../paths/helpers";

import {
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces/requirement";
import flatten from "lodash/flatten";
import { Tabs } from "../../../../components/Tabs";
import { useQueryEngagementsListAll } from "../../../../query/engagements/queries/useQueryEngagementsListAll";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { WS_LINKS } from "../../../../types/wsLinks";
import { openInNewTab } from "../../../../utils/openInNewTab";
import { RequirementDefinitionsTable } from "../../components/RequirementsTable";
import styles from "./styles.module.scss";

const RequirementDefinitionsTab: React.FC<{
  requirementDefinitionStatus?: RequirementDefinitionStatus;
  listPath: string;
  isMainTab?: boolean;
}> = ({ requirementDefinitionStatus, listPath, isMainTab }) => {
  const history = useHistory();

  const requirementsDefinitionsListQuery = useQueryRequirementDefinitionList({
    filter: {
      status: requirementDefinitionStatus,
      requirementType: {
        notIn: [
          RequirementType.Registration,
          RequirementType.PayoutMethod,
          RequirementType.TaxVerification
        ]
      }
    },
    sort: {
      // status: "asc"
      updatedAt: "desc"
    }
  });
  const engagementsListAllQuery = useQueryEngagementsListAll();

  return (
    <WSQueries
      queries={{
        requirementsDefinitionsListQuery,
        engagementsListAllQuery
      }}
    >
      {({
        requirementsDefinitionsListQueryData,
        engagementsListAllQueryData
      }) => {
        const list = flatten(
          requirementsDefinitionsListQueryData.pages.map(r => r.data) || []
        );
        const listSize =
          requirementsDefinitionsListQueryData.pages[0]?.summary?.listSize || 0;

        return (
          <>
            <WSFlexBox.CenterY mb="L" className={styles.statsContainer}>
              <WSElement className={styles.resultsStats}>
                <WSText.ParagraphXs color="gray500">
                  Showing:
                </WSText.ParagraphXs>
                <WSChip
                  pillText={`${listSize} items`}
                  theme="neutral"
                  pillType="noBackground"
                />
              </WSElement>
            </WSFlexBox.CenterY>
            {list.length === 0 ? (
              <WSEmptyState
                py="3XL"
                type="documents"
                title="No requirements found"
                description={
                  isMainTab
                    ? "Once you create requirements, they will appear here."
                    : undefined
                }
                hasBorder
                bgColor="gray"
                actions={
                  isMainTab
                    ? {
                        buttons: [
                          {
                            label: "Create requirement",
                            kind: "Primary",
                            onClick: () => {
                              history.push(
                                getCreateRequirementDefinitionPathname()
                              );
                            }
                          },
                          {
                            label: "Learn about requirements",
                            kind: "Secondary",
                            onClick: () =>
                              openInNewTab(WS_LINKS.eligibilityRequirements)
                          }
                        ]
                      }
                    : undefined
                }
              />
            ) : (
              <WSInfiniteScroll
                onLoad={() => {
                  requirementsDefinitionsListQuery.fetchNextPage();
                }}
                loadMore={
                  requirementsDefinitionsListQuery.data &&
                  requirementsDefinitionsListQuery.data.pages.length > 0
                }
                endOfList={!requirementsDefinitionsListQuery.hasNextPage}
                loading={!!requirementsDefinitionsListQuery.isFetchingNextPage}
              >
                <RequirementDefinitionsTable
                  listPath={listPath}
                  requirements={list}
                  engagements={engagementsListAllQueryData}
                />
              </WSInfiniteScroll>
            )}
          </>
        );
      }}
    </WSQueries>
  );
};

export const RouteRequirements: React.FC = () => {
  const history = useHistory();
  useBrowserPageTitle("Engagement requirements");

  const queryActiveRequirementDefinitionsSummary =
    useQueryRequirementDefinitionListSize({
      filter: {
        status: RequirementDefinitionStatus.Active,
        requirementType: {
          notIn: [
            RequirementType.Registration,
            RequirementType.PayoutMethod,
            RequirementType.TaxVerification
          ]
        }
      }
    });

  const queryArchivedRequirementDefinitionsSummary =
    useQueryRequirementDefinitionListSize({
      filter: {
        status: RequirementDefinitionStatus.Inactive
      }
    });

  return (
    <WSPage>
      <WSPageToolbar
        title={"Engagement requirements"}
        children={
          <WSText.ParagraphSm color="gray500" style={{ maxWidth: "550px" }}>
            Engagement requirements can be added to engagements. Contractors
            must complete these requirements to be eligible for payment for
            those engagements.
          </WSText.ParagraphSm>
        }
        actions={[
          {
            label: "Create requirement",
            onClick: async () => {
              history.push(getCreateRequirementDefinitionPathname());
            },
            buttonKind: "Primary"
          }
        ]}
      />

      <WSQueries
        queries={{
          queryActiveRequirementDefinitionsSummary,
          queryArchivedRequirementDefinitionsSummary
        }}
      >
        {({
          queryActiveRequirementDefinitionsSummaryData,
          queryArchivedRequirementDefinitionsSummaryData
        }) => {
          const tabs = [
            {
              label: "Active",
              path: getSectionPathByRequirementStatus(
                RequirementDefinitionStatus.Active
              ),
              badge: queryActiveRequirementDefinitionsSummaryData,
              requirementDefinitionStatus: RequirementDefinitionStatus.Active,
              isMainTab: true
            },
            {
              label: "Archived",
              path: getSectionPathByRequirementStatus(
                RequirementDefinitionStatus.Inactive
              ),
              badge: queryArchivedRequirementDefinitionsSummaryData,
              requirementDefinitionStatus: RequirementDefinitionStatus.Inactive
            }
          ];

          return (
            <>
              <Tabs mb="L" tabs={tabs} />
              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.path}
                    path={tab.path}
                    render={() => (
                      <RequirementDefinitionsTab
                        requirementDefinitionStatus={
                          tab.requirementDefinitionStatus
                        }
                        listPath={tab.path}
                        isMainTab={tab.isMainTab}
                      />
                    )}
                  />
                ))}
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
